/**
 * Alpine.js component for managing products and filters on a product listing page.
 * Features include:
 * - Loading color swatches from global variable.
 * - Initialization with default values and fetching initial data.
 * - Watching for changes in active facets and sort options to trigger product queries.
 * - Handling pagination and loading more products.
 * - Generating facet subtext based on facet values.
 * - Constructing query parameters for product filtering.
 * - Updating URL with query parameters.
 * - Clearing all active facets.
 * - Removing individual filters.
 * - Computing the count of applied filters.
 */

const COLLECTION_FILTER = window.COLLECTION_FILTER;
const RESULTS_PER_PAGE = 18;
const diff = DeepDiff.diff;
const loadSwatches = () => {
  const normalizeSwatch = (swatch) => {
    if (swatch.color_image) return { image: swatch.color_image };
    if (swatch.color_gradient) return { gradient: swatch.color_gradient };
    return { color: swatch.color };
  };
  return Object.fromEntries((window.COLOR_SWATCHES || []).map((swatch) => [swatch.heading, normalizeSwatch(swatch)]));
};
const productsBrowser = (collectionHandle, context = 'collection') => ({
  data: null,
  context: 'collection',
  isLoading: false,
  isLoadingInitial: true,
  currentPage: 1,
  ignoreUpdates: false,
  loadId: 0,
  error: '',
  colorSwatches: loadSwatches(),
  dataFacets: [],
  activeFacets: {},
  activeFacetsOld: [],
  filtersDesktopVisible: COLLECTION_FILTER,
  availableFilters: [],
  totalProductsCount: 0,
  afterCursor: null,
  filters: {},
  sort: '',
  sortOld: '',
  displaySecondVariant: null,
  sortingOptions: [
    { field: 'MANUAL', label: 'Featured', direction: 'desc', reverse: false },
    { field: 'BEST_SELLING', direction: 'desc', label: 'Bestsellers', reverse: false },
    { field: 'PRICE', direction: 'asc', label: 'Price Low to High', reverse: false },
  ].filter(Boolean),

  async init() {
    this.debounceNextTick = createDebouncer(this.$nextTick.bind(this));
    this.setStateFromUrl();
    try {
      this.dataFacets = await fetchFilters(collectionHandle);
      await this.queryProducts();
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      this.error = error.toString();
    } finally {
      this.isLoadingInitial = false;
    }

    this.$watch(
      'activeFacets',
      this.debounceNextTick((value) => {
        // we have the code that in some cases updated the activeFacets immediately
        // so we wait one tick before fetching products

        if (this.ignoreUpdates) return;
        if (this.hasNoActiveFacetsAndEmptyResults) return;

        const differences = diff(this.activeFacetsOld, value);
        if (differences) {
          this.currentPage = 1;
          this.queryProducts();
        }
      }),
    );

    this.$watch('ignoreUpdates', (value) => {
      if (value) {
        this.activeFacetsCopy = this.activeFacets;
        this.sortCopy = this.sort;
      } else {
        this.activeFacets = this.activeFacetsCopy;
        this.sort = this.sortCopy;
      }
    });

    this.$watch('sort', (value) => {
      if (value === this.sortOld) return;
      if (this.ignoreUpdates) return;

      this.queryProducts();
    });
  },

  getFacetSubtext(value) {
    const testValue = value.toLowerCase();
    switch (testValue) {
      case 'room for 1':
        return 'Up to 330 lbs (150 kg)';
      case 'room for 2':
        return 'Up to 400 lbs (180 kg)';
      case 'room for 3':
        return 'Up to 550 lbs (250 kg)';
      case 'performance acrylic':
        return '150,000 loops of ultra-soft acrylic yarn';
      case '100% natural cotton':
        return 'T-shirt soft natural cotton';
      default:
        return null;
    }
  },

  async queryProducts({ loadMore = false } = {}) {
    const loadId = ++this.loadId;

    const [sortField, sortDirection] = this.sort.split('::');

    const searchParams = new URLSearchParams({
      page: this.currentPage,
    });

    let reverse = false; // to reverse the price sorting
    if (sortField && sortDirection) {
      searchParams.append(`sort.${sortField}`, sortDirection);
      if (sortField === 'PRICE' && sortDirection === 'desc') {
        reverse = true;
      }
    }

    const filtersArray = [];
    // take the activefacets and convert into filtersArray which would be fed into the collection query
    Object.entries(this.activeFacets).forEach(([key, values]) => {
      // filtersObj will have value when we go for filtering but not when we are reloading the page.
      if (values && values.length) {
        values.forEach((value) => {
          const productMetafieldRegex = new RegExp(/filter.p.m.([\w]+).([\w-]+)/);
          if (key.match(productMetafieldRegex)) {
            // eslint-disable-next-line
            const [_, namespace, mfKey] = key.match(productMetafieldRegex);
            filtersArray.push({ productMetafield: { namespace, key: mfKey, value } });
            searchParams.append(key, value);
          }
          const variantMetafieldRegex = new RegExp(/filter.v.m.([\w]+).([\w-]+)/);
          if (key.match(variantMetafieldRegex)) {
            // eslint-disable-next-line
            const [_, namespace, mfKey] = key.match(variantMetafieldRegex);
            filtersArray.push({ variantMetafield: { namespace, key: mfKey, value } });
            searchParams.append(key, value);
          }
          if (key === 'filter.v.availability') {
            const valueBoolean = value === 'In stock';
            filtersArray.push({ available: valueBoolean });
            searchParams.append(key, value);
          }
          if (key === 'filter.v.price') {
            const obj = typeof value === 'string' ? JSON.parse(value) : value;
            filtersArray.push({ price: { min: +obj.min, max: +obj.max } });
            searchParams.append(`${key}.min`, obj.min);
            searchParams.append(`${key}.max`, obj.max);
          }
          if (key === 'filter.v.price.min' || key === 'filter.v.price.max') {
            // find if there's a key by price if not add to the filtersArray otherwise update the value
            const priceFilter = filtersArray.find((filter) => filter.price);
            if (!priceFilter) {
              if (key === 'filter.v.price.min') {
                filtersArray.push({ price: { min: value, max: 0 } });
              }
              filtersArray.push({ price: { min: 0, max: 0 } });
            }
            if (priceFilter) {
              if (key === 'filter.v.price.min') {
                priceFilter.price.min = +value;
              } else {
                priceFilter.price.max = +value;
              }
            }
          }
        });
      }
    });

    this.activeFacetsOld = this.activeFacets;
    this.sortOld = this.sort;

    try {
      this.isLoading = true;
      this.error = '';

      const args = [
        collectionHandle,
        loadMore ? this.afterCursor : null,
        filtersArray,
        RESULTS_PER_PAGE,
        sortField,
        reverse,
      ];

      const data = await fetchFilteredCollection(...args);

      const oldData = this.data;
      this.$dispatch('products-browser-data', { searchParams, data });
      this.data = data;
      this.displaySecondVariant = this.data.displaySecondVariant?.value;
      if (this.data && this.data.collectionLinkCard) {
        const collectionCardData = await fetchCollectionData(this.data.collectionLinkCard.value);
        this.collectionCard = collectionCardData;
      }
      if (loadMore) {
        this.data.products = [...oldData.products, ...this.data.products];
      }
      this.activeFacets = { ...this.getEmptyFacets(), ...this.activeFacets };
      this.sortOld = this.sort;
      if (this.data.pageInfo.hasNextPage) {
        this.afterCursor = this.data.pageInfo.endCursor;
      }
      if (!loadMore) {
        const countArgs = [collectionHandle, loadMore ? this.afterCursor : null, filtersArray, 50, sortField, reverse];
        const countData = await fetchFilteredCollection(...countArgs);
        this.totalProductsCount = countData.products.length;
      }

      this.updateUrl(searchParams);
    } catch (e) {
      // TODO: analytics? sentry?
      this.error = e.message;
      throw this.error;
    } finally {
      if (loadId === this.loadId) {
        this.isLoading = false;
      }
    }
  },

  get hasLoadMore() {
    return this.data.pageInfo.hasNextPage;
  },

  loadMore() {
    this.currentPage += 1;
    return this.queryProducts({ loadMore: true });
  },

  get facets() {
    const filters = this.dataFacets;
    const result = filters.map((facet) => {
      facet.expanded = true;
      if (facet.id === 'filter.p.m.filter.swatch_color') {
        facet.type = 'SWATCH';
        facet.expanded = true;
        const colorSwatches = keyBy(window.COLOR_SWATCHES, 'heading');
        facet.values = facet.values.map((value) => {
          return {
            ...value,
            swatch: colorSwatches[JSON.parse(value.input).productMetafield.value],
          };
        });
      } else if (facet.id === 'filter.v.availability') {
        facet.type = 'RADIO';
      }

      return facet;
    });
    this.filtersObj = keyBy(result, 'id');
    return result || [];
  },

  getEmptyFacets() {
    return Object.fromEntries(this.facets.map((filter) => [filter.id, []]));
  },

  get products() {
    let availableProducts = this.data.products;
    availableProducts = availableProducts.filter(product => {
        return product.disableSwatch !== 'true'
    })
    return availableProducts;
  },

  get hasNoActiveFacetsAndEmptyResults() {
    return this.products.length === 0 && Object.values(this.activeFacets).every((value) => !value.length);
  },

  updateUrl(searchParams) {
    const url = new URL(window.location.href);
    const currentParams = new URLSearchParams(window.location.search);

    url.search = new URLSearchParams(searchParams);

    const firstSort = this.sortingOptions[0];
    if (searchParams.get(`sort.${firstSort.field}`) === firstSort.direction) {
      url.searchParams.delete(`sort.${firstSort.field}`);
    }

    Array.from(url.searchParams.keys()).forEach((key) => {
      if (key.startsWith('bgfilter.')) {
        url.searchParams.delete(key);
      }
    });

    url.searchParams.delete('page'); // no classic pagination

    const currentParamsEntries = Array.from(currentParams.entries());
    const newParamsEntries = Array.from(url.searchParams.entries());

    if (
      currentParamsEntries.length !== newParamsEntries.length ||
      currentParamsEntries.some(([, value], index) => value !== newParamsEntries[index][1])
    ) {
      window.history.pushState({}, '', url.toString());
    }
  },

  setStateFromUrl() {
    const activeFacets = this.data ? this.getEmptyFacets() : {};
    this.currentPage = 1;

    const firstSort = this.sortingOptions[0];
    if (firstSort) {
      this.sort = `${firstSort.field}::${firstSort.direction}`;
    }
    /* we need to recreate the object that comes with the activeFacets.
     * so for productMetafields the object is {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}}
     * for variantOption the object is {variantOption: {name: 'size', value: 'S'}}
     * convert filter.p.m.custom.category => {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}}
     * convert filter.v.option.size => variantOption: {name: 'size', value: 'S'}}
     * activeFacets is an object like this
     * {
     * filter.p.m.custom.category: ['Bags']
     * filter.v.option.size: []
     * }
     */
    // we can write simple Regex to convert filter.p.m.custom.category => {productMetafield: {namespace: 'custom', key: 'category', value: '<coming from URL param>'}
    Array.from(new URLSearchParams(window.location.search)).forEach(([key, value]) => {
      if (key.startsWith('filter.')) {
        if (key.includes('filter.v.price')) {
          if (!activeFacets['filter.v.price']) {
            activeFacets['filter.v.price'] = [];
          }
          const priceRegex = new RegExp(/filter.v.price.([\w]+)/);
          const [, priceKey] = key.match(priceRegex);
          activeFacets['filter.v.price'].length === 0
            ? activeFacets['filter.v.price'].push({ [priceKey]: +value })
            : (activeFacets['filter.v.price'][0][priceKey] = +value);
        } else if (!activeFacets[key]) {
          activeFacets[key] = [value];
        } else {
          activeFacets[key].push(value);
        }
      } else if (key.startsWith('sort.')) {
        const sortField = key.replace('sort.', '');
        const sortDirection = value;
        this.sort = `${sortField}::${sortDirection}`;
      } else if (key === 'page') {
        // ignore page from url
        // this.currentPage = Number(value);
      }
    });

    this.activeFacets = activeFacets;
  },

  clearAllFacets() {
    this.activeFacets = this.getEmptyFacets();
    this.queryProducts();
  },

  removeFilter(filter) {
    if (Array.isArray(this.activeFacets[filter.field])) {
      this.activeFacets[filter.field] = this.activeFacets[filter.field].filter((v) => v !== filter.value);
    } else {
      this.activeFacets[filter.field] = [];
    }
  },

  get appliedFilters() {
    /**
     * Expected Object Structure
     * {
     *  "p.m.category": ['Bags', 'Shoes'],
     *  "p.m.color": ['Black', 'Blue']
     * }
     * Output
     * [
     *   { field: "p.m.category", value: "Bags" },
     *   { field: "p.m.category", value: "Shoes" },
     *   { field: "p.m.color", value: "Black" },
     *   { field: "p.m.color", value: "Blue" },
     * ]
     */
    const appliedFilters = Object.entries(this.activeFacets).reduce((acc, facet) => {
      // key = p.m.category & value = ['Bags', 'Shoes']
      acc.push(...Object.values(facet[1]).map((value) => ({ field: facet[0], value })));
      return acc;
    }, []);
    return appliedFilters;
  },

  get facetCount() {
    return this.appliedFilters.length;
  },
});

document.addEventListener('alpine:init', () => {
  Alpine.data('productsBrowser', productsBrowser);
});
